import { useViewjobEmployerDossierQuery } from "./__generated__/viewjobEmployerDossier.gqlgenerated";
import { FloatingAskCareerScoutStarters } from "./components/core/AskCareerScoutStarters/FloatingAskCareerScoutStarters";
import {
  CAREER_SCOUT_ALWAYS_AWARE_BUCKETS,
  useCareerScoutAlwaysAwareBucket,
} from "./components/core/AskCareerScoutStarters/proctor";
import { EmployerInsightsPopoverProvider } from "./components/core/JobComponent/EmployerInsights/EmployerInsightsPopover";
import {
  StickyButtonAccessory,
  StickyButtonsRoot,
} from "./components/core/JobComponent/StickyButtons/StickyButtons";
import { OGWrappedViewJobMainContent } from "./components/core/JobComponent/ViewJobMainContent/ViewJobMainContent";
import { WebToAppPromo } from "./components/core/WebToAppPromo";
import { ApplicantInsightsModalContextProvider } from "./components/mosaic/ApplicantInsights/context/ApplicantInsightsModalContextProvider";
import { DwellTimingProvider } from "./context/DwellTiming";
import { EmployerOutreachProvider } from "./context/EmployerOutreachContext";
import {
  useViewjobOneGraphModel,
  useViewjobSourceUrl,
  useProctorGroups,
} from "./context/ViewJobDataContext";
import { useIsDesktopDisplay } from "./context/ViewJobDisplayContext";
import { useConversationStarterCleanup } from "./hooks/useConversationStarterCleanup";
import { isJobFromEmployerOutreach } from "./hooks/useEmployerOutreachSource";
import { useViewJobLoadLogging } from "./hooks/useViewJobLoadLogging";
import { CareerScoutLoggingContextProvider } from "../../ace/logging/CareerScoutLoggingContext";
import { VisibilityTrackerProvider } from "../../shared/logging/visibility-tracker";
import { useTareData } from "../../shared/native-modules/tare/TareDataContext";

import React from "react";
import { useWindowDimensions } from "react-native";

export interface Props {
  url?: string | null;
  reload: () => void;
  navigationStartTimeMs: number;
  fetchStartTimeMs: number | null;
  fetchEndTimeMs: number | null;
  pageLoadSignalAlreadyEmitted?: boolean;
}

function ModalContextProvider({ children }: { children: React.ReactNode }) {
  return (
    <ApplicantInsightsModalContextProvider>
      <EmployerInsightsPopoverProvider>{children}</EmployerInsightsPopoverProvider>
    </ApplicantInsightsModalContextProvider>
  );
}

export function AppVJLoadedContent({
  reload,
  navigationStartTimeMs,
  fetchStartTimeMs,
  fetchEndTimeMs,
  pageLoadSignalAlreadyEmitted = false,
}: Props): React.JSX.Element {
  const { height, width, scale } = useWindowDimensions();
  const ogModel = useViewjobOneGraphModel();
  const tareData = useTareData();
  const isDesktopDisplay = useIsDesktopDisplay();
  const isAppBtnSticky = ogModel?.isAppBtnSticky ?? true;
  const isScrollVariant =
    useCareerScoutAlwaysAwareBucket() === CAREER_SCOUT_ALWAYS_AWARE_BUCKETS.SCROLL;

  const url = useViewjobSourceUrl();
  const proctorGroups = useProctorGroups();
  useViewJobLoadLogging({
    viewJobModel: ogModel,
    navigationStartTimeMs,
    fetchStartTimeMs,
    fetchEndTimeMs,
    proctorGroups,
    pageLoadSignalAlreadyEmitted,
    viewport: { height, width, scale },
  });

  // Use ogModel as the primary interface for jobKey and mobvjtk
  const jobKey = ogModel?.job.jobKey;
  const mobvjtk = ogModel?.mobvjtk;
  const isFromEmployerOutreach = isJobFromEmployerOutreach(url ?? "");

  useConversationStarterCleanup();

  useViewjobEmployerDossierQuery({
    variables: {
      input: jobKey || "",
    },
    skip: !jobKey || !isFromEmployerOutreach,
  });

  return (
    <CareerScoutLoggingContextProvider
      accountId={tareData?.accountStatus?.accountId ?? null}
      proctorGroups={proctorGroups ?? null}
      ctk={ogModel?.ctk ?? null}
    >
      <ModalContextProvider>
        <DwellTimingProvider
          tk={mobvjtk || null}
          pgtype={isDesktopDisplay ? "desktopVj" : "mobvj"}
          accountId={tareData?.accountStatus?.accountId || null}
          jobKey={jobKey || null}
          hasCompanySummary={!!ogModel?.employerInsightsModel?.companySummary}
        >
          {/* Make sure VisibilityProvider is the parent of StickyButtonRoot, since it expects a ScrollView as a descendent */}
          <VisibilityTrackerProvider viewportDimensions={{ width, height }}>
            <EmployerOutreachProvider>
              <StickyButtonsRoot needSticky={isAppBtnSticky}>
                <OGWrappedViewJobMainContent reload={reload} />
                {isScrollVariant && (
                  <StickyButtonAccessory>
                    <FloatingAskCareerScoutStarters />
                  </StickyButtonAccessory>
                )}
              </StickyButtonsRoot>
            </EmployerOutreachProvider>
          </VisibilityTrackerProvider>
          <WebToAppPromo />
        </DwellTimingProvider>
      </ModalContextProvider>
    </CareerScoutLoggingContextProvider>
  );
}
